import _globalThis from "@babel/runtime-corejs3/core-js/global-this";
import _Symbol$for from "@babel/runtime-corejs3/core-js/symbol/for";
import _Array$isArray from "@babel/runtime-corejs3/core-js/array/is-array";
import _someInstanceProperty from "@babel/runtime-corejs3/core-js/instance/some";
import _filterInstanceProperty from "@babel/runtime-corejs3/core-js/instance/filter";
import React from "react";
import { useClasses } from "@xo-union/react-css-modules";
import noop from "lodash/noop";
import styles from "./styles.css";

const toClassList = classesString => classesString.split(/\s+/); // When globalThis is used with array access Babel does not insert the polyfill.
// This alias is defined as a workaround.


const globalAlias = _globalThis;

const $noScrollMountCount = _Symbol$for('@xo-union/ui-accessibility/no-scroll-mounts');

globalAlias[$noScrollMountCount] = globalAlias[$noScrollMountCount] || 0; // Render refs are useful to keep a reference to the latest rendered value
// Without causing a re-render of hooks that depend on the latest value (Such as effects)

const useRenderRef = value => {
  const ref = React.useRef(value);
  ref.current = value;
  return ref;
};

export const useNoScrollEffect = function () {
  let {
    classes: classesProp,
    enabled = true
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const classes = useClasses(styles, classesProp);
  const noScrollClassRef = useRenderRef(classes['no-scroll']);
  const enabledRef = useRenderRef(enabled);
  React.useEffect(() => {
    if (!enabled) {
      globalAlias[$noScrollMountCount] += 1;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const noScrollClass = noScrollClassRef.current;

    const handleClasses = () => {
      if (globalAlias[$noScrollMountCount] > 0) {
        document.body.classList.add(...toClassList(noScrollClass));
      } else if (globalAlias[$noScrollMountCount] === 0) {
        document.body.classList.remove(...toClassList(noScrollClass));
      } else {
        throw new Error('No scroll counter should never be less than 0');
      }
    };

    if (enabled) {
      globalAlias[$noScrollMountCount] += 1;
    } else {
      globalAlias[$noScrollMountCount] -= 1;
    }

    handleClasses();
    return () => {
      // Latest ref value is needed in cleanup
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const latestEnabledValue = enabledRef.current;

      if (enabled && latestEnabledValue) {
        // If unmounting without becoming disabled
        globalAlias[$noScrollMountCount] -= 1;
      }

      handleClasses();
    };
  }, [enabled, enabledRef, noScrollClassRef]);
};
export const useGlobalKeyboardEscapeEffect = _ref => {
  let {
    enabled = true,
    onEscapeKey
  } = _ref;
  React.useEffect(() => {
    const handleKeyDown = event => {
      if (event.key !== 'Escape') return;
      onEscapeKey(event);
    };

    const setup = () => {
      document.addEventListener('keydown', handleKeyDown);
    };

    const teardown = () => {
      document.removeEventListener('keydown', handleKeyDown);
    };

    if (enabled) {
      setup();
    } else {
      teardown();
    }

    return teardown;
  }, [enabled, onEscapeKey]);
};
export const useOutsideClickEffect = (_ref2, targetRefOrRefs) => {
  let {
    enabled = true,
    onClickOutside
  } = _ref2;
  React.useEffect(() => {
    const validRefsFilter = targetRef => !!(targetRef !== null && targetRef !== void 0 && targetRef.current);

    const handleClick = event => {
      var _context;

      const normalizedRefs = _Array$isArray(targetRefOrRefs) ? targetRefOrRefs : [targetRefOrRefs];

      if (_someInstanceProperty(_context = _filterInstanceProperty(normalizedRefs).call(normalizedRefs, validRefsFilter)).call(_context, targetRef => targetRef.current.contains(event.target))) {
        return;
      }

      onClickOutside(event);
    };

    const setup = () => {
      document.addEventListener('click', handleClick, {
        capture: true
      });
    };

    const teardown = () => {
      document.removeEventListener('click', handleClick, {
        capture: true
      });
    };

    if (enabled) {
      setup();
    } else {
      teardown();
    }

    return teardown;
  }, [enabled, onClickOutside, targetRefOrRefs]);
};

const useModalEffects = (_ref3, dialogRef) => {
  let {
    onClose = noop,
    onClickOutside = noop,
    onEscapeKey = noop,
    classes,
    enabled = true
  } = _ref3;
  useNoScrollEffect({
    enabled,
    classes
  });
  useGlobalKeyboardEscapeEffect({
    enabled,
    onEscapeKey: React.useCallback(event => {
      onEscapeKey(event);
      onClose(event);
    }, [onClose, onEscapeKey])
  });
  useOutsideClickEffect({
    enabled,
    onClickOutside: React.useCallback(event => {
      onClickOutside(event);
      onClose(event);
    }, [onClose, onClickOutside])
  }, dialogRef);
};

export default useModalEffects;